import { useCallback, useEffect, useState } from "react";
import { Box, Typography, Button, Modal, TextField } from "@mui/material";

const TreatmentHistoryCard = ({ patientId }) => {
  const [data, setData] = useState([]);
  const fetchData = useCallback(async () => {
    if (!patientId) return;

    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/patients/${patientId}/treatment_history`;
      const data = await fetch(url, {
        credentials: "include",
        method: "GET",
      });
      setData(await data.json());
    } catch (error) {
      console.log("Error fetching timeline data:", error);
    }
  }, [patientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const [editId, setEditId] = useState();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalNote, setModalNote] = useState("");
  const closeModal = useCallback(() => {
    setEditId(undefined)
    setModalOpen(false)
    setModalNote("")
  }, []);

  const submitModal = useCallback(async () => {
    let url = editId ?
      `${process.env.REACT_APP_BACKEND_URL}/patients/${patientId}/treatment_history/${editId}` :
      `${process.env.REACT_APP_BACKEND_URL}/patients/${patientId}/treatment_history`;

    await fetch(url, {
      credentials: "include",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        notes: modalNote,
      }),
    });

    await fetchData();
    closeModal();
  }, [patientId, editId, modalNote]);

  const editNote = useCallback((note) => {
    setModalNote(note.notes);
    setEditId(note.id);
    setModalOpen(true);
  }, [patientId]);

  const deleteNote = useCallback(async (note) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/patients/${patientId}/treatment_history/${note.id}`;
    await fetch(url, {
      credentials: "include",
      method: "DELETE",
    });

    await fetchData();
  }, [patientId]);

  return (
    <Box id="timeline-container">
      <Box className="my-patients-topbar" mt={3}>
        <Typography className="title" component="h4">
          Treatment History
        </Typography>
        <Button
          variant="contained"
          color="info"
          className="btn-primary"
          sx={{ fontWeight: "bold" }}
          onClick={() => setModalOpen(true)}
        >
          Add Note
        </Button>
      </Box>
      {(data.length === 0) ? (
        <Box
          display="flex"
          alignItems="center"
          bgcolor={"#042835"}
          justifyContent="center"
          p={5}
        >
          <Typography component="h4" className="text-primery" align="center">
            No notes
          </Typography>
        </Box>
      ) : data.map((item, i) => (
        <Box
          key={item.id}
          sx={{
            display: "flex",
            flexDirection: "row",
            bgcolor: "#042835",
            borderBottom: (i !== data.length - 1) ? "1px solid rgb(255, 255, 255, 50%)" : undefined,
            p: "12px",
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              color: "var(--primeryColor)",
              flex: 1,
              textAlign: "left",
              whiteSpace: "break-spaces",
            }}
          >
            {item.notes}
          </Typography>
          <Box>
            <Button
              variant="contained"
              color="info"
              className="btn-primary"
              onClick={() => editNote(item)}
              sx={{
                marginRight: "8px",
              }}
            >
              Edit
            </Button>
            <Button
              variant="contained"
              color="error"
              className="btn-delete"
              onClick={() => deleteNote(item)}
            >
              Delete
            </Button>
          </Box>
        </Box>
      ))}
      <Modal
        open={modalOpen}
        onClose={closeModal}
        className="profile-modal"
      >
        <Box sx={style}>
          <Box className="modal-shadow">
            <Box className="modal-header">
              <Typography
                id="modal-modal-title"
                className="text-primery"
                variant="h4"
                component="h4"
              >
                {editId ? "Edit Note" : "Add Note"}
              </Typography>
              <Button onClick={closeModal}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                >
                  <path
                    opacity="0.2"
                    d="M32.0002 3.138L28.8632 0L16.4242 12.439L3.98517 0L0.847168 3.138L13.2862 15.577L0.847168 28.016L3.98517 31.154L16.4242 18.714L28.8632 31.153L32.0012 28.015L19.5612 15.577L32.0002 3.138Z"
                    fill="white"
                  />
                </svg>
              </Button>
            </Box>
            <Box
              className="modal-body"
              id="modal-modal-description"
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                alignItems: "center",
              }}
            >
              <TextField
                multiline
                minRows={6}
                maxRows={24}
                value={modalNote}
                onChange={e => setModalNote(e.target.value)}
                className="input-box"
                sx={{
                  color: "white",
                  ".MuiOutlinedInput-root": {
                    color: "white",
                  },
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--whiteLight)",
                  },
                }}
              />
              <Button
                variant="contained"
                color="info"
                className="btn-primary"
                sx={{
                  fontWeight: "bold",
                  maxWidth: "200px",
                  width: "100%",
                }}
                onClick={submitModal}
              >
                Submit
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default TreatmentHistoryCard;

const style = {
  position: "absolute",
  top: "50%",
  left: 15,
  right: 15,
  transform: "translateY(-50%)",
  maxWidth: 1000,
  color: "#ffffff",
  boxShadow: 24,
  margin: "1rem auto",
  maxHeight: "100%",
};